import mqtt from 'mqtt';
import { store, RootState } from './store';
import { todosSlice } from './store/todos';
import type {
    Task,
    StatusGroup,
    TodoDetails,
    MqttMessage,
    UpdateTodoMqttPayload,
    UpdateTodoReportMqttPayload,
    ArchiveTodoMqttPayload,
    RestoreTodoMqttPayload,
    UpdateTaskStatusMqttPayload,
    AddTaskImageMqttPayload,
    DeleteTaskImageMqttPayload,
    AddTaskLinkMqttPayload,
    DeleteTaskLinkMqttPayload,
    AddTaskCommentMqttPayload,
    UpdateTaskCommentMqttPayload,
    DeleteTaskCommentMqttPayload,
    AddTaskCommentImageMqttPayload,
    DeleteTaskCommentImageMqttPayload,
    UpdateTaskMqttPayload,
    AddTaskMqttPayload,
    DeleteTaskMqttPayload,
    UpdateTaskPriorityMqttPayload,
    UpdateTaskTagsMqttPayload,
} from '../types';

import {
    TASK_STATUS,
    MQTT_HOST,
    MQTT_LOGIN,
    MQTT_PASSWORD,
    MQTT_TOPIC,
} from './constants';

let mqttClient: mqtt.MqttClient | null = null;

const findTask = (groups: StatusGroup[], id: number): Task  | null => {
    for (const group of groups) {
        for (const task of group.tasks) {
            if (Number(task.taskId) === Number(id)) {
                return task;
            }
        }
    }
    return null;
};

function removeTodoFromActiveLists(todoId: number, preferredProjectUrl?: string) {
    const state = store.getState() as RootState;
    const queries = state[todosSlice.reducerPath]?.queries || {};
    const projectUrls = new Set<string>();

    if (preferredProjectUrl) {
        projectUrls.add(preferredProjectUrl);
    }

    Object.values(queries).forEach((entry: any) => {
        const argsUrl = entry?.originalArgs?.projectUrl;
        if (entry?.endpointName === 'getTodos' && argsUrl) {
            projectUrls.add(argsUrl);
        }

        const todos = entry?.data?.message?.todos;
        if (Array.isArray(todos) && todos.some((t: any) => Number(t.todoId) === Number(todoId))) {
            if (argsUrl) {
                projectUrls.add(argsUrl);
            }
        }
    });

    if (projectUrls.size === 0 && preferredProjectUrl) {
        projectUrls.add(preferredProjectUrl);
    }

    projectUrls.forEach((projectUrl) => {
        store.dispatch(
            todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                if (!draft?.message?.todos) {
                    return;
                }
                draft.message.todos = draft.message.todos.filter(
                    (t) => Number(t.todoId) !== Number(todoId),
                );
            }),
        );
    });

    // Подстраховка: refetch активного списка с сервера (эпик уже archivedAt в БД)
    store.dispatch(todosSlice.util.invalidateTags(['Todos', 'ArchivedTodos']));
}

export const mqttService = {
    init: () => {
        if (mqttClient) {
            mqttClient.end();
            mqttClient = null;
        }

        const handleMessage = (raw: string) => {
            try {
                const mqttMessage: MqttMessage = JSON.parse(raw);
                const { type, action, meta: payload } = mqttMessage;
                console.log('MQTT MESSAGE', mqttMessage);
                const { todoId } = payload;

                if (action === 'todo') {
                    switch (type) {
                        case 'addNewTodo': {
                            store.dispatch(todosSlice.util.updateQueryData('getTodos', { projectUrl: payload.projectUrl }, (draft) => {
                                if (!draft?.message?.todos) {
                                    return;
                                }
                                if (!draft.message.todos.some((t) => Number(t.todoId) === Number(todoId))) {
                                    draft.message.todos.unshift({
                                        todoId: +todoId,
                                        todoName: payload.todoName,
                                        trackerKey: payload.trackerKey ?? null,
                                        reportPath: payload.reportPath ?? null,
                                    });
                                }
                            }));
                            break;
                        }

                        case 'updateTodo': {
                            const { todoName, trackerKey } = payload as UpdateTodoMqttPayload;
                            store.dispatch(todosSlice.util.updateQueryData('getTodos', { projectUrl: payload.projectUrl }, (draft) => {
                                if (!draft?.message?.todos) {
                                    return;
                                }
                                const todo = draft.message.todos.find((t) => Number(t.todoId) === Number(todoId));
                                if (todo) {
                                    todo.todoName = todoName;
                                    if (trackerKey !== undefined) {
                                        todo.trackerKey = trackerKey;
                                    }
                                }
                            }));
                            break;
                        }

                        case 'updateTodoReport': {
                            const { reportPath, projectUrl } = payload as UpdateTodoReportMqttPayload;
                            store.dispatch(todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                                if (!draft?.message?.todos) {
                                    return;
                                }
                                const todo = draft.message.todos.find((t) => Number(t.todoId) === Number(todoId));
                                if (todo) {
                                    todo.reportPath = reportPath ?? null;
                                }
                            }));
                            store.dispatch(todosSlice.util.updateQueryData('getArchivedTodos', { projectUrl }, (draft) => {
                                if (!draft?.message?.todos) {
                                    return;
                                }
                                const todo = draft.message.todos.find((t) => Number(t.todoId) === Number(todoId));
                                if (todo) {
                                    todo.reportPath = reportPath ?? null;
                                }
                            }));
                            store.dispatch(todosSlice.util.updateQueryData('getTodoById', { todoId: +todoId }, (draft: TodoDetails) => {
                                draft.reportPath = reportPath ?? null;
                            }));
                            break;
                        }

                        case 'archiveTodo': {
                            const archivePayload = payload as ArchiveTodoMqttPayload;
                            removeTodoFromActiveLists(+todoId, archivePayload.projectUrl);

                            store.dispatch(
                                todosSlice.util.updateQueryData(
                                    'getArchivedTodos',
                                    { projectUrl: archivePayload.projectUrl },
                                    (draft) => {
                                        if (!draft?.message?.todos) {
                                            return;
                                        }
                                        if (!draft.message.todos.some((t) => Number(t.todoId) === Number(todoId))) {
                                            draft.message.todos.unshift({
                                                todoId: +todoId,
                                                todoName: archivePayload.todoName,
                                                archivedAt: archivePayload.archivedAt || new Date().toISOString(),
                                                trackerKey: archivePayload.trackerKey ?? null,
                                            });
                                        }
                                    },
                                ),
                            );

                            store.dispatch(
                                todosSlice.util.updateQueryData(
                                    'getTodoById',
                                    { todoId: +todoId },
                                    (draft: TodoDetails) => {
                                        if (!draft) {
                                            return;
                                        }
                                        draft.archivedAt = archivePayload.archivedAt || new Date().toISOString();
                                    },
                                ),
                            );
                            break;
                        }

                        case 'restoreTodo': {
                            const { todoName, trackerKey, projectUrl } = payload as RestoreTodoMqttPayload;
                            store.dispatch(todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                                if (!draft?.message?.todos) {
                                    return;
                                }
                                const existing = draft.message.todos.find(
                                    (t) => Number(t.todoId) === Number(todoId),
                                );
                                if (existing) {
                                    if (todoName) {
                                        existing.todoName = todoName;
                                    }
                                    if (trackerKey !== undefined) {
                                        existing.trackerKey = trackerKey ?? null;
                                    }
                                } else {
                                    draft.message.todos.unshift({
                                        todoId: +todoId,
                                        todoName,
                                        trackerKey: trackerKey ?? null,
                                    });
                                }
                            }));

                            store.dispatch(
                                todosSlice.util.updateQueryData(
                                    'getArchivedTodos',
                                    { projectUrl },
                                    (draft) => {
                                        if (!draft?.message?.todos) {
                                            return;
                                        }
                                        draft.message.todos = draft.message.todos.filter(
                                            (t) => Number(t.todoId) !== Number(todoId),
                                        );
                                    },
                                ),
                            );

                            store.dispatch(
                                todosSlice.util.updateQueryData(
                                    'getTodoById',
                                    { todoId: +todoId },
                                    (draft: TodoDetails) => {
                                        if (!draft) {
                                            return;
                                        }
                                        draft.archivedAt = null;
                                    },
                                ),
                            );
                            store.dispatch(todosSlice.util.invalidateTags(['Todos', 'ArchivedTodos']));
                            break;
                        }
                    }
                }

                if (action === 'task') {
                    store.dispatch(todosSlice.util.updateQueryData('getTodoById', { todoId: +todoId }, (draft: TodoDetails) => {
                        switch (type) {
                            case 'addTask': {
                                const { taskId, whoAddTask, taskName, taskContent, user, uuid } = payload as AddTaskMqttPayload;
                                const group = draft.groups.find(group => group.status === TASK_STATUS.open);
                                if (group) {
                                    const existing = group.tasks.find((t) => Number(t.taskId) === Number(taskId));
                                    if (existing) {
                                        if (!existing.user && user) {
                                            existing.user = user;
                                        }
                                        if (!existing.taskName && taskName) {
                                            existing.taskName = taskName;
                                        }
                                        break;
                                    }
                                    group.tasks.unshift({
                                        taskId,
                                        todoId,
                                        uuid,
                                        tags: [],
                                        status: 1,
                                        priority: null,
                                        taskName: taskName || '',
                                        taskContent,
                                        whoAddTask,
                                        links: [],
                                        images: [],
                                        comments: [],
                                        user,
                                    });
                                }
                                break;
                            }
                            case 'updateTask': {
                                const { taskId, taskName, taskContent } = payload as UpdateTaskMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task) {
                                    if (taskName !== undefined && taskName !== null) {
                                        task.taskName = taskName;
                                    }
                                    if (taskContent !== undefined && taskContent !== null) {
                                        task.taskContent = taskContent;
                                    }
                                }
                                break;
                            }
                            case 'deleteTask': {
                                const { taskId } = payload as DeleteTaskMqttPayload;
                                const group = draft.groups.find(group => group.tasks.find(t => t.taskId === taskId));
                                if (group) {
                                    group.tasks = group.tasks.filter(t => t.taskId !== taskId);
                                }
                                break;
                            }
                            case 'updateStatus': {
                                const { taskId, status } = payload as UpdateTaskStatusMqttPayload;
                                const oldGroup = draft.groups.find((group) => (
                                    group.tasks.find((t) => Number(t.taskId) === Number(taskId))
                                ));
                                if (oldGroup) {
                                    if (Number(oldGroup.status) === Number(status)) {
                                        break;
                                    }

                                    const task = oldGroup.tasks.find((t) => Number(t.taskId) === Number(taskId))!;
                                    oldGroup.tasks = oldGroup.tasks.filter((t) => Number(t.taskId) !== Number(taskId));
                                    task.status = status;

                                    const newGroup = draft.groups.find((group) => Number(group.status) === Number(status));
                                    if (newGroup) {
                                        newGroup.tasks.unshift(task);
                                    }
                                }

                                break;
                            }
                            case 'updatePriority': {
                                const { taskId, priority } = payload as UpdateTaskPriorityMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task) {
                                    task.priority = priority;
                                }
                                break;
                            }
                            case 'addImage': {
                                const { taskId, imageId, imagePath } = payload as AddTaskImageMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task) {
                                    if (!Array.isArray(task.images)) {
                                        task.images = [];
                                    }
                                    if (!task.images.some((img) => Number(img.imageId) === Number(imageId))) {
                                        task.images.push({ imageId: Number(imageId), imagePath });
                                    }
                                }
                                break;
                            }
                            case 'deleteImage': {
                                const { taskId, imageId } = payload as DeleteTaskImageMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task && Array.isArray(task.images)) {
                                    task.images = task.images.filter(
                                        (img) => Number(img.imageId) !== Number(imageId),
                                    );
                                }
                                break;
                            }
                            case 'addLink': {
                                const { taskId, linkId, linkPath } = payload as AddTaskLinkMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task) {
                                    if (!Array.isArray(task.links)) {
                                        task.links = [];
                                    }
                                    if (!task.links.some((l) => Number(l.linkId) === Number(linkId))) {
                                        task.links.push({ linkId: Number(linkId), linkPath });
                                    }
                                }
                                break;
                            }
                            case 'deleteLink': {
                                const { taskId, linkId } = payload as DeleteTaskLinkMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task && Array.isArray(task.links)) {
                                    task.links = task.links.filter(
                                        (link) => Number(link.linkId) !== Number(linkId),
                                    );
                                }
                                break;
                            }
                            case 'addComment': {
                                const { taskId, commentId, comment, createDate, updatedAt, images, user } = payload as AddTaskCommentMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task) {
                                    if (!Array.isArray(task.comments)) {
                                        task.comments = [];
                                    }
                                    if (!task.comments.some((c) => Number(c.commentId) === Number(commentId))) {
                                        task.comments.push({
                                            commentId: Number(commentId),
                                            comment,
                                            createDate,
                                            updatedAt,
                                            images: images || [],
                                            user,
                                        });
                                    }
                                }
                                break;
                            }
                            case 'updateComment': {
                                const { taskId, commentId, comment, updatedAt } = payload as UpdateTaskCommentMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                const item = task?.comments?.find(
                                    (c) => Number(c.commentId) === Number(commentId),
                                );
                                if (item) {
                                    item.comment = comment;
                                    item.updatedAt = updatedAt;
                                }
                                break;
                            }
                            case 'deleteComment': {
                                const { taskId, commentId } = payload as DeleteTaskCommentMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task && Array.isArray(task.comments)) {
                                    task.comments = task.comments.filter(
                                        (c) => Number(c.commentId) !== Number(commentId),
                                    );
                                }
                                break;
                            }
                            case 'addCommentImage': {
                                const { taskId, commentId, imageId, imagePath } = payload as AddTaskCommentImageMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                const item = task?.comments?.find(
                                    (c) => Number(c.commentId) === Number(commentId),
                                );
                                if (item) {
                                    if (!Array.isArray(item.images)) {
                                        item.images = [];
                                    }
                                    if (!item.images.some((img) => Number(img.imageId) === Number(imageId))) {
                                        item.images.push({
                                            imageId: Number(imageId),
                                            imagePath,
                                        });
                                    }
                                }
                                break;
                            }
                            case 'deleteCommentImage': {
                                const { taskId, commentId, imageId } = payload as DeleteTaskCommentImageMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                const item = task?.comments?.find(
                                    (c) => Number(c.commentId) === Number(commentId),
                                );
                                if (item?.images) {
                                    item.images = item.images.filter(
                                        (img) => Number(img.imageId) !== Number(imageId),
                                    );
                                }
                                break;
                            }
                            case 'updateTags': {
                                const { taskId, tags } = payload as UpdateTaskTagsMqttPayload;
                                const task = findTask(draft.groups, taskId);
                                if (task) {
                                    task.tags = tags;
                                }
                                break;
                            }
                            default:
                                console.warn('Неизвестное действие MQTT:', type);
                        }
                    }));
                }
            } catch (error) {
                console.error('Ошибка обработки MQTT-сообщения:', error);
            }
        };

        mqttClient = mqtt.connect(MQTT_HOST, {
            username: MQTT_LOGIN,
            password: MQTT_PASSWORD,
            reconnectPeriod: 1000,
            protocolVersion: 4,
        });

        mqttClient.on('connect', () => {
            console.log('Подключено к MQTT брокеру', MQTT_HOST);
            mqttClient?.subscribe(MQTT_TOPIC, { qos: 1 }, (err) => {
                if (err) {
                    console.error('Ошибка подписки:', err);
                } else {
                    console.log('Подписались на топик', MQTT_TOPIC);
                }
            });
        });

        mqttClient.on('message', (_topic, message) => {
            handleMessage(message.toString());
        });

        mqttClient.on('error', (error) => {
            console.error('Ошибка MQTT:', error);
        });

        mqttClient.on('close', () => {
            console.log('Отключено от MQTT брокера');
        });
    },

    disconnect: () => {
        if (mqttClient) {
            mqttClient.end();
            mqttClient = null;
        }
    },
};
